import React, { useState } from 'react';
import { FileText, Plus, Trash2, Download, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import ContractPDFPreview from './contract_pdf_preview';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ContractFormApp = () => {
  const [formData, setFormData] = useState({
    contract_title: 'Freelance Services Agreement',
    logo_url: 'https://supabase.horoboro.com/storage/v1/object/public/horoboro-website/website-images/homepage/animated-logo/Animaz30060.webp',
    agreement_date: '',
    client_name: '',
    client_address: '',
    client_email: '',
    client_phone: '',
    client_vat: '',
    freelancer_name: 'Ariel Monti',
    freelancer_address: 'Gerberweg 61, Nidau 2560, Switzerland',
    freelancer_email: 'ariel@horoboro.com',
    freelancer_phone: 'xxxx',
    freelancer_ahv: 'xxxxxx',
    freelancer_vat: '',
    freelancer_business_reg: '',
    services_description: '',
    deliverables: [],
    milestones: [],
    term_effective_date: '',
    term_end_date: '',
    compensation_total: '',
    compensation_structure: '',
    hourly_rate: '',
    estimated_hours: '',
    monthly_retainer: '',
    payment_schedule: [],
    invoice_frequency: '',
    invoice_frequency_custom: '',
    approved_expenses: '',
    portfolio_permitted: true,
    portfolio_conditions: '',
    work_product_ownership: 'shared',
    insurance_amount: '',
    additional_compensation: '',
    other_client_restrictions: '',
    non_compete_applies: false,
    non_compete_duration: '',
    non_compete_geography: '',
    non_compete_industry: '',
    non_compete_compensation: '',
    processes_personal_data: false,
    data_processing_role: '',
    dpa_required: false,
    jurisdiction_type: '',
    jurisdiction_location: '',
    number_of_arbitrators: '',
    proceeding_language: '',
    contract_language: '',
    business_day_canton: '',
    client_notice_physical: '',
    client_notice_email: '',
    freelancer_notice_physical: '',
    freelancer_notice_email: '',
    schedule_a_services: '',
    schedule_a_deliverables: [],
    schedule_a_technical: '',
    schedule_a_timeline: '',
    schedule_a_dependencies: '',
    schedule_a_exclusions: '',
    schedule_b_included: false,
    schedule_b_content: '',
    client_signatory_name: '',
    client_signatory_title: '',
    client_signature_date: '',
    freelancer_signatory_name: '',
    freelancer_signature_date: ''
  });

  const [currentSection, setCurrentSection] = useState(0);
  const [errors, setErrors] = useState({});
   const [showPreview, setShowPreview] = useState(false);

const generatePDFDirect = async (elementId) => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      alert('Contract content not found');
      return;
    }

    const pages = element.querySelectorAll('.page');
    if (pages.length === 0) {
      alert('No pages found');
      return;
    }

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      
      const canvas = await html2canvas(page, {
        scale: 2, // Back to 2 for crisp text
        useCORS: true,
        logging: false,
        width: page.offsetWidth,
        height: page.offsetHeight
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92); // 92% quality - sweet spot
      const imgWidth = 210;
      const imgHeight = 297;

      if (i > 0) {
        pdf.addPage();
      }

      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
    }

    pdf.save(`Swiss_Freelance_Contract_${formData.client_name}_${formData.freelancer_name}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF. Please try again.');
  }
};


const sections = [
  { id: 'parties', title: 'Parties Information', icon: '👥' },
  { id: 'scope', title: 'Scope of Work', icon: '📋' },
  { id: 'term', title: 'Term & Timeline', icon: '📅' },
  { id: 'compensation', title: 'Compensation', icon: '💰' },
  { id: 'ip', title: 'Intellectual Property', icon: '💡' },
  { id: 'contractor', title: 'Contractor Status', icon: '🏢' },
  { id: 'non_compete', title: 'Non-Competition', icon: '🚫' },
  { id: 'data', title: 'Data Protection', icon: '🔒' },
  { id: 'dispute', title: 'Dispute Resolution', icon: '⚖️' },
  { id: 'general', title: 'General Provisions', icon: '📄' },
  { id: 'schedules', title: 'Schedules', icon: '📎' },
  { id: 'signatures', title: 'Signatures', icon: '✍️' },
  { id: 'branding', title: 'Branding', icon: '🎨' }  // Add this line
];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addArrayItem = (arrayName, template) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], template]
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const validateSection = (sectionId) => {
    const newErrors = {};
    
    if (sectionId === 'parties') {
      if (!formData.agreement_date) newErrors.agreement_date = 'Required';
      if (!formData.client_name) newErrors.client_name = 'Required';
      if (!formData.client_address) newErrors.client_address = 'Required';
      if (!formData.client_email) newErrors.client_email = 'Required';
      if (!formData.client_phone) newErrors.client_phone = 'Required';
      if (!formData.freelancer_name) newErrors.freelancer_name = 'Required';
      if (!formData.freelancer_address) newErrors.freelancer_address = 'Required';
      if (!formData.freelancer_email) newErrors.freelancer_email = 'Required';
      if (!formData.freelancer_phone) newErrors.freelancer_phone = 'Required';
      if (!formData.freelancer_ahv) newErrors.freelancer_ahv = 'Required';
    }
    
    if (sectionId === 'scope') {
      if (!formData.services_description) newErrors.services_description = 'Required';
      if (formData.deliverables.length === 0) newErrors.deliverables = 'At least one deliverable required';
    }
    
    if (sectionId === 'term') {
      if (!formData.term_effective_date) newErrors.term_effective_date = 'Required';
      if (!formData.term_end_date) newErrors.term_end_date = 'Required';
    }
    
    if (sectionId === 'compensation') {
      if (!formData.compensation_total) newErrors.compensation_total = 'Required';
      if (!formData.compensation_structure) newErrors.compensation_structure = 'Required';
      if (!formData.invoice_frequency) newErrors.invoice_frequency = 'Required';
      if (formData.payment_schedule.length === 0) newErrors.payment_schedule = 'At least one payment required';
    }

    if (sectionId === 'dispute') {
      if (!formData.jurisdiction_type) newErrors.jurisdiction_type = 'Required';
      if (!formData.jurisdiction_location) newErrors.jurisdiction_location = 'Required';
      if (!formData.proceeding_language) newErrors.proceeding_language = 'Required';
    }

    if (sectionId === 'general') {
      if (!formData.contract_language) newErrors.contract_language = 'Required';
      if (!formData.business_day_canton) newErrors.business_day_canton = 'Required';
      if (!formData.client_notice_physical) newErrors.client_notice_physical = 'Required';
      if (!formData.client_notice_email) newErrors.client_notice_email = 'Required';
      if (!formData.freelancer_notice_physical) newErrors.freelancer_notice_physical = 'Required';
      if (!formData.freelancer_notice_email) newErrors.freelancer_notice_email = 'Required';
    }

    if (sectionId === 'schedules') {
      if (!formData.schedule_a_services) newErrors.schedule_a_services = 'Required';
      if (!formData.schedule_a_timeline) newErrors.schedule_a_timeline = 'Required';
    }

    if (sectionId === 'signatures') {
      if (!formData.client_signatory_name) newErrors.client_signatory_name = 'Required';
      if (!formData.client_signatory_title) newErrors.client_signatory_title = 'Required';
      if (!formData.client_signature_date) newErrors.client_signature_date = 'Required';
      if (!formData.freelancer_signatory_name) newErrors.freelancer_signatory_name = 'Required';
      if (!formData.freelancer_signature_date) newErrors.freelancer_signature_date = 'Required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextSection = () => {
    if (validateSection(sections[currentSection].id)) {
      setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
    }
  };

  const prevSection = () => {
    setCurrentSection(prev => Math.max(prev - 1, 0));
  };

  const generateContract = () => {
    // Validate all sections
    let allValid = true;
    sections.forEach(section => {
      if (!validateSection(section.id)) {
        allValid = false;
      }
    });

    if (!allValid) {
      alert('Please complete all required fields');
      return;
    }

    // Generate contract text
    const contract = generateContractText(formData);
    
    // Download as text file
    const blob = new Blob([contract], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Swiss_Freelance_Contract_${formData.client_name}_${formData.freelancer_name}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateContractText = (data) => {
    return `FREELANCE SERVICES AGREEMENT
(Swiss Law)

Agreement Date: ${data.agreement_date}

1. PARTIES

1.1 Client
Name: ${data.client_name}
Registered Address: ${data.client_address}
Email: ${data.client_email}
Phone: ${data.client_phone}
VAT Number: ${data.client_vat || 'N/A'}

1.2 Freelancer
Name: ${data.freelancer_name}
Address: ${data.freelancer_address}
Email: ${data.freelancer_email}
Phone: ${data.freelancer_phone}
AHV Number: ${data.freelancer_ahv}
VAT Number: ${data.freelancer_vat || 'N/A'}
Business Registration: ${data.freelancer_business_reg || 'N/A'}

2. SCOPE OF WORK

2.1 Services Description
${data.services_description}

2.2 Deliverables
${data.deliverables.map((d, i) => `${i + 1}. ${d.name} - Due: ${d.date}`).join('\n')}

2.3 Milestones
${data.milestones.map((m, i) => `${i + 1}. ${m.name} - ${m.description} - Target: ${m.date}`).join('\n')}

3. TERM AND TERMINATION

Effective Date: ${data.term_effective_date}
End Date: ${data.term_end_date}

4. COMPENSATION AND PAYMENT

Total Compensation: CHF ${data.compensation_total}
Structure: ${data.compensation_structure}
${data.compensation_structure === 'hourly_rate' ? `Hourly Rate: CHF ${data.hourly_rate} (Est. ${data.estimated_hours} hours)` : ''}
${data.compensation_structure === 'monthly_retainer' ? `Monthly Retainer: CHF ${data.monthly_retainer}` : ''}

Payment Schedule:
${data.payment_schedule.map((p, i) => `Payment ${i + 1}: CHF ${p.amount} - ${p.trigger}`).join('\n')}

Invoice Frequency: ${data.invoice_frequency === 'custom' ? data.invoice_frequency_custom : data.invoice_frequency}

${data.approved_expenses ? `Approved Expenses: ${data.approved_expenses}` : ''}
${data.additional_compensation ? `Additional Compensation: ${data.additional_compensation}` : ''}

5. INTELLECTUAL PROPERTY RIGHTS
Work Product Ownership: ${data.work_product_ownership === 'client' ? 'Client owns all work product upon payment' : data.work_product_ownership === 'freelancer' ? 'Freelancer retains ownership, Client receives license' : 'Shared ownership as specified'}
Portfolio Use Permitted: ${data.portfolio_permitted ? 'Yes' : 'No'}
${data.portfolio_permitted && data.portfolio_conditions ? `Conditions: ${data.portfolio_conditions}` : ''}

7. INDEPENDENT CONTRACTOR STATUS
${data.insurance_amount ? `Required Insurance: CHF ${data.insurance_amount}` : ''}
${data.other_client_restrictions ? `Client Restrictions: ${data.other_client_restrictions}` : ''}

10. NON-COMPETITION
Applies: ${data.non_compete_applies ? 'Yes' : 'No'}
${data.non_compete_applies ? `
Duration: ${data.non_compete_duration} months
Geographic Area: ${data.non_compete_geography}
Industry: ${data.non_compete_industry}
Compensation: ${data.non_compete_compensation}
` : ''}

11. DATA PROTECTION
Processes Personal Data: ${data.processes_personal_data ? 'Yes' : 'No'}
${data.processes_personal_data ? `Role: ${data.data_processing_role}` : ''}
${data.dpa_required ? 'DPA Required: Yes' : ''}

13. DISPUTE RESOLUTION
Type: ${data.jurisdiction_type === 'state_courts' ? 'State Courts' : 'Arbitration'}
Location: ${data.jurisdiction_location}
${data.jurisdiction_type === 'arbitration' ? `Arbitrators: ${data.number_of_arbitrators}` : ''}
Language: ${data.proceeding_language}

14. GENERAL PROVISIONS
Contract Language: ${data.contract_language}
Business Day Canton: ${data.business_day_canton}

Client Notice Address:
${data.client_notice_physical}
Email: ${data.client_notice_email}

Freelancer Notice Address:
${data.freelancer_notice_physical}
Email: ${data.freelancer_notice_email}

SCHEDULE A: SCOPE OF WORK DETAILS

Detailed Services:
${data.schedule_a_services}

Specific Deliverables:
${data.schedule_a_deliverables.map((d, i) => `
${i + 1}. ${d.deliverable}
   Specification: ${d.specification}
   Format: ${d.format}
   Acceptance: ${d.acceptance}
`).join('\n')}

Technical Requirements:
${data.schedule_a_technical || 'N/A'}

Timeline:
${data.schedule_a_timeline}

Dependencies:
${data.schedule_a_dependencies || 'N/A'}

Exclusions:
${data.schedule_a_exclusions || 'N/A'}

${data.schedule_b_included ? `
SCHEDULE B: DATA PROCESSING AGREEMENT
${data.schedule_b_content}
` : ''}

SIGNATURES

CLIENT
Name: ${data.client_signatory_name}
Title: ${data.client_signatory_title}
Date: ${data.client_signature_date}

FREELANCER
Name: ${data.freelancer_signatory_name}
Date: ${data.freelancer_signature_date}

---
Note: This is a simplified version. The complete contract includes all standard legal provisions as outlined in the full template.
Generated on: ${new Date().toLocaleDateString()}
`;
  };

  const renderPartiesSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Agreement Date *</label>
        <input
          type="date"
          value={formData.agreement_date}
          onChange={(e) => handleChange('agreement_date', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.agreement_date ? 'border-red-500' : 'border-gray-300'}`}
        />
        {errors.agreement_date && <p className="text-red-500 text-sm mt-1">{errors.agreement_date}</p>}
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Client Information</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Client Name *</label>
            <input
              type="text"
              value={formData.client_name}
              onChange={(e) => handleChange('client_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.client_name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Company Name"
            />
            {errors.client_name && <p className="text-red-500 text-sm mt-1">{errors.client_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Registered Address *</label>
            <input
              type="text"
              value={formData.client_address}
              onChange={(e) => handleChange('client_address', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.client_address ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Street, City, Postal Code"
            />
            {errors.client_address && <p className="text-red-500 text-sm mt-1">{errors.client_address}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                value={formData.client_email}
                onChange={(e) => handleChange('client_email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${errors.client_email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="email@company.com"
              />
              {errors.client_email && <p className="text-red-500 text-sm mt-1">{errors.client_email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Phone *</label>
              <input
                type="tel"
                value={formData.client_phone}
                onChange={(e) => handleChange('client_phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${errors.client_phone ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="+41 XX XXX XX XX"
              />
              {errors.client_phone && <p className="text-red-500 text-sm mt-1">{errors.client_phone}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">VAT Number (optional)</label>
            <input
              type="text"
              value={formData.client_vat}
              onChange={(e) => handleChange('client_vat', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="CHE-XXX.XXX.XXX"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Freelancer Information</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Freelancer Name *</label>
            <input
              type="text"
              value={formData.freelancer_name}
              onChange={(e) => handleChange('freelancer_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Full Name"
            />
            {errors.freelancer_name && <p className="text-red-500 text-sm mt-1">{errors.freelancer_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Address *</label>
            <input
              type="text"
              value={formData.freelancer_address}
              onChange={(e) => handleChange('freelancer_address', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_address ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Street, City, Postal Code"
            />
            {errors.freelancer_address && <p className="text-red-500 text-sm mt-1">{errors.freelancer_address}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                value={formData.freelancer_email}
                onChange={(e) => handleChange('freelancer_email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="email@example.com"
              />
              {errors.freelancer_email && <p className="text-red-500 text-sm mt-1">{errors.freelancer_email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Phone *</label>
              <input
                type="tel"
                value={formData.freelancer_phone}
                onChange={(e) => handleChange('freelancer_phone', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_phone ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="+41 XX XXX XX XX"
              />
              {errors.freelancer_phone && <p className="text-red-500 text-sm mt-1">{errors.freelancer_phone}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">AHV Number *</label>
            <input
              type="text"
              value={formData.freelancer_ahv}
              onChange={(e) => handleChange('freelancer_ahv', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_ahv ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="756.XXXX.XXXX.XX"
            />
            {errors.freelancer_ahv && <p className="text-red-500 text-sm mt-1">{errors.freelancer_ahv}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">VAT Number (optional)</label>
              <input
                type="text"
                value={formData.freelancer_vat}
                onChange={(e) => handleChange('freelancer_vat', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="CHE-XXX.XXX.XXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Business Registration (optional)</label>
              <input
                type="text"
                value={formData.freelancer_business_reg}
                onChange={(e) => handleChange('freelancer_business_reg', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Registration Number"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderScopeSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Services Description *</label>
        <textarea
          value={formData.services_description}
          onChange={(e) => handleChange('services_description', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg h-32 ${errors.services_description ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Describe the services to be provided..."
        />
        {errors.services_description && <p className="text-red-500 text-sm mt-1">{errors.services_description}</p>}
      </div>

      <div className="border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Deliverables *</h3>
          <button
            onClick={() => addArrayItem('deliverables', { name: '', date: '' })}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            Add Deliverable
          </button>
        </div>
        
        {errors.deliverables && <p className="text-red-500 text-sm mb-2">{errors.deliverables}</p>}
        
        {formData.deliverables.map((deliverable, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">Deliverable {index + 1}</span>
              <button
                onClick={() => removeArrayItem('deliverables', index)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={deliverable.name}
                  onChange={(e) => updateArrayItem('deliverables', index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Website Design"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Target Date</label>
                <input
                  type="date"
                  value={deliverable.date}
                  onChange={(e) => updateArrayItem('deliverables', index, 'date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Milestones (optional)</h3>
          <button
            onClick={() => addArrayItem('milestones', { name: '', description: '', date: '' })}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            Add Milestone
          </button>
        </div>
        
        {formData.milestones.map((milestone, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">Milestone {index + 1}</span>
              <button
                onClick={() => removeArrayItem('milestones', index)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={milestone.name}
                  onChange={(e) => updateArrayItem('milestones', index, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Phase 1 Completion"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Description</label>
                <input
                  type="text"
                  value={milestone.description}
                  onChange={(e) => updateArrayItem('milestones', index, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="Brief description"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Target Date</label>
                <input
                  type="date"
                  value={milestone.date}
                  onChange={(e) => updateArrayItem('milestones', index, 'date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTermSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Effective Date *</label>
          <input
            type="date"
            value={formData.term_effective_date}
            onChange={(e) => handleChange('term_effective_date', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg ${errors.term_effective_date ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.term_effective_date && <p className="text-red-500 text-sm mt-1">{errors.term_effective_date}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">End Date *</label>
          <input
            type="date"
            value={formData.term_end_date}
            onChange={(e) => handleChange('term_end_date', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg ${errors.term_end_date ? 'border-red-500' : 'border-gray-300'}`}
          />
          {errors.term_end_date && <p className="text-red-500 text-sm mt-1">{errors.term_end_date}</p>}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Either party may terminate this agreement with 30 days notice as specified in the contract terms.
        </p>
      </div>
    </div>
  );

  const renderCompensationSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Total Compensation (CHF) *</label>
        <input
          type="number"
          value={formData.compensation_total}
          onChange={(e) => handleChange('compensation_total', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.compensation_total ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="50000"
        />
        {errors.compensation_total && <p className="text-red-500 text-sm mt-1">{errors.compensation_total}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Compensation Structure *</label>
        <select
          value={formData.compensation_structure}
          onChange={(e) => handleChange('compensation_structure', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.compensation_structure ? 'border-red-500' : 'border-gray-300'}`}
        >
          <option value="">Select structure...</option>
          <option value="fixed_price">Fixed Price</option>
          <option value="hourly_rate">Hourly Rate</option>
          <option value="monthly_retainer">Monthly Retainer</option>
          <option value="milestone_based">Milestone Based</option>
        </select>
        {errors.compensation_structure && <p className="text-red-500 text-sm mt-1">{errors.compensation_structure}</p>}
      </div>

      {formData.compensation_structure === 'hourly_rate' && (
        <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-sm font-medium mb-2">Hourly Rate (CHF)</label>
            <input
              type="number"
              value={formData.hourly_rate}
              onChange={(e) => handleChange('hourly_rate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="150"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Estimated Hours</label>
            <input
              type="number"
              value={formData.estimated_hours}
              onChange={(e) => handleChange('estimated_hours', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="200"
            />
          </div>
        </div>
      )}

      {formData.compensation_structure === 'monthly_retainer' && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium mb-2">Monthly Retainer (CHF)</label>
          <input
            type="number"
            value={formData.monthly_retainer}
            onChange={(e) => handleChange('monthly_retainer', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="5000"
          />
        </div>
      )}

      <div className="border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Payment Schedule *</h3>
          <button
            onClick={() => addArrayItem('payment_schedule', { amount: '', trigger: '' })}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            Add Payment
          </button>
        </div>

        {errors.payment_schedule && <p className="text-red-500 text-sm mb-2">{errors.payment_schedule}</p>}
        
        {formData.payment_schedule.map((payment, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">Payment {index + 1}</span>
              <button
                onClick={() => removeArrayItem('payment_schedule', index)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">Amount (CHF)</label>
                <input
                  type="number"
                  value={payment.amount}
                  onChange={(e) => updateArrayItem('payment_schedule', index, 'amount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="25000"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Due Date/Trigger</label>
                <input
                  type="text"
                  value={payment.trigger}
                  onChange={(e) => updateArrayItem('payment_schedule', index, 'trigger', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Upon contract signing"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Invoice Frequency *</label>
        <select
          value={formData.invoice_frequency}
          onChange={(e) => handleChange('invoice_frequency', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.invoice_frequency ? 'border-red-500' : 'border-gray-300'}`}
        >
          <option value="">Select frequency...</option>
          <option value="monthly">Monthly</option>
          <option value="upon_milestone_completion">Upon Milestone Completion</option>
          <option value="upon_project_completion">Upon Project Completion</option>
          <option value="bi_weekly">Bi-weekly</option>
          <option value="custom">Custom</option>
        </select>
        {errors.invoice_frequency && <p className="text-red-500 text-sm mt-1">{errors.invoice_frequency}</p>}
      </div>

      {formData.invoice_frequency === 'custom' && (
        <div>
          <label className="block text-sm font-medium mb-2">Custom Invoice Frequency</label>
          <input
            type="text"
            value={formData.invoice_frequency_custom}
            onChange={(e) => handleChange('invoice_frequency_custom', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Describe custom frequency"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">Pre-approved Expense Categories (optional)</label>
        <textarea
          value={formData.approved_expenses}
          onChange={(e) => handleChange('approved_expenses', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
          placeholder="e.g., Travel expenses, software licenses, stock photos..."
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Additional Compensation (optional)</h3>
        <p className="text-sm text-gray-600 mb-3">
          Specify any performance-based or additional compensation beyond the base amount
        </p>
        <textarea
          value={formData.additional_compensation}
          onChange={(e) => handleChange('additional_compensation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
          placeholder="e.g., 50% of website sales, 10% revenue share, performance bonuses, equity compensation..."
        />
      </div>
    </div>
  );

  const renderIPSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Work Product Ownership *</label>
        <select
          value={formData.work_product_ownership}
          onChange={(e) => handleChange('work_product_ownership', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="client">Client owns all work product upon payment (standard)</option>
          <option value="freelancer">Freelancer retains ownership, Client receives license</option>
          <option value="shared">Shared ownership (specify in conditions)</option>
        </select>
        <p className="text-sm text-gray-600 mt-2">
          {formData.work_product_ownership === 'client' && 'All work created will be owned exclusively by the Client once payment is received.'}
          {formData.work_product_ownership === 'freelancer' && 'Freelancer keeps ownership but grants Client a license to use the work.'}
          {formData.work_product_ownership === 'shared' && 'Specify shared ownership terms in the conditions below.'}
        </p>
      </div>

      {formData.work_product_ownership !== 'client' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Non-standard ownership structures may require additional legal review and more detailed specifications.
          </p>
        </div>
      )}

      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.portfolio_permitted}
            onChange={(e) => handleChange('portfolio_permitted', e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Allow Freelancer to use work in portfolio</span>
        </label>
      </div>

      {(formData.portfolio_permitted || formData.work_product_ownership !== 'client') && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {formData.work_product_ownership === 'client' ? 'Portfolio Use Conditions' : 'Ownership & Usage Conditions'}
          </label>
          <textarea
            value={formData.portfolio_conditions}
            onChange={(e) => handleChange('portfolio_conditions', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
            placeholder={
              formData.work_product_ownership === 'client'
                ? "e.g., Only after public launch, anonymized versions only, exclude confidential sections..."
                : "e.g., Specify license terms, usage rights, attribution requirements, restrictions..."
            }
          />
        </div>
      )}
    </div>
  );

  const renderContractorSection = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Important:</strong> The contract establishes the Freelancer as an independent contractor, not an employee. The Freelancer is responsible for their own taxes, AHV, and insurance.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Required Professional Liability Insurance (CHF) - optional</label>
        <input
          type="number"
          value={formData.insurance_amount}
          onChange={(e) => handleChange('insurance_amount', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          placeholder="e.g., 100000"
        />
        <p className="text-sm text-gray-600 mt-1">Leave empty if no specific insurance requirement</p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Restrictions on Other Clients (optional)</label>
        <textarea
          value={formData.other_client_restrictions}
          onChange={(e) => handleChange('other_client_restrictions', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
          placeholder="e.g., No direct competitors during contract period..."
        />
      </div>
    </div>
  );

  const renderNonCompeteSection = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-800">
          <strong>Note:</strong> Non-competition clauses in Switzerland are strictly regulated (max 3 years) and may require compensation. Consider whether this is necessary for your engagement.
        </p>
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.non_compete_applies}
            onChange={(e) => handleChange('non_compete_applies', e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Apply Non-Competition Restrictions</span>
        </label>
      </div>

      {formData.non_compete_applies && (
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-sm font-medium mb-2">Duration (months, max 36)</label>
            <input
              type="number"
              max="36"
              value={formData.non_compete_duration}
              onChange={(e) => handleChange('non_compete_duration', Math.min(36, e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="12"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Geographic Area</label>
            <input
              type="text"
              value={formData.non_compete_geography}
              onChange={(e) => handleChange('non_compete_geography', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Switzerland, Canton Zurich"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Industry/Sector</label>
            <input
              type="text"
              value={formData.non_compete_industry}
              onChange={(e) => handleChange('non_compete_industry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Financial technology, E-commerce"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Compensation for Restriction</label>
            <input
              type="text"
              value={formData.non_compete_compensation}
              onChange={(e) => handleChange('non_compete_compensation', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="e.g., Included in base compensation / CHF 5000 additional"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderDataSection = () => (
    <div className="space-y-6">
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.processes_personal_data}
            onChange={(e) => handleChange('processes_personal_data', e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium">Freelancer will process personal data</span>
        </label>
      </div>

      {formData.processes_personal_data && (
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-sm font-medium mb-2">Data Processing Role</label>
            <select
              value={formData.data_processing_role}
              onChange={(e) => handleChange('data_processing_role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select role...</option>
              <option value="processor">Data Processor (processes on behalf of Client)</option>
              <option value="independent_controller">Independent Controller</option>
            </select>
          </div>

          {formData.data_processing_role === 'processor' && (
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.dpa_required}
                  onChange={(e) => handleChange('dpa_required', e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Include Data Processing Agreement (Schedule B)</span>
              </label>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              The contract includes standard FADP/GDPR compliance provisions including security measures, breach notification, and data subject rights.
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderDisputeSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Jurisdiction Type *</label>
        <select
          value={formData.jurisdiction_type}
          onChange={(e) => handleChange('jurisdiction_type', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.jurisdiction_type ? 'border-red-500' : 'border-gray-300'}`}
        >
          <option value="">Select type...</option>
          <option value="state_courts">State Courts</option>
          <option value="arbitration">Arbitration</option>
        </select>
        {errors.jurisdiction_type && <p className="text-red-500 text-sm mt-1">{errors.jurisdiction_type}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {formData.jurisdiction_type === 'state_courts' ? 'Canton' : 'Seat of Arbitration'} *
        </label>
        <input
          type="text"
          value={formData.jurisdiction_location}
          onChange={(e) => handleChange('jurisdiction_location', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.jurisdiction_location ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="e.g., Zurich"
        />
        {errors.jurisdiction_location && <p className="text-red-500 text-sm mt-1">{errors.jurisdiction_location}</p>}
      </div>

      {formData.jurisdiction_type === 'arbitration' && (
        <div>
          <label className="block text-sm font-medium mb-2">Number of Arbitrators</label>
          <select
            value={formData.number_of_arbitrators}
            onChange={(e) => handleChange('number_of_arbitrators', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select...</option>
            <option value="1">1</option>
            <option value="3">3</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">Language of Proceedings *</label>
        <select
          value={formData.proceeding_language}
          onChange={(e) => handleChange('proceeding_language', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.proceeding_language ? 'border-red-500' : 'border-gray-300'}`}
        >
          <option value="">Select language...</option>
          <option value="German">German</option>
          <option value="French">French</option>
          <option value="Italian">Italian</option>
          <option value="English">English</option>
        </select>
        {errors.proceeding_language && <p className="text-red-500 text-sm mt-1">{errors.proceeding_language}</p>}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> The contract includes a 30-day negotiation requirement before formal proceedings. Both parties must attempt good faith resolution first.
        </p>
      </div>
    </div>
  );

  const renderGeneralSection = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Governing Contract Language *</label>
        <select
          value={formData.contract_language}
          onChange={(e) => handleChange('contract_language', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.contract_language ? 'border-red-500' : 'border-gray-300'}`}
        >
          <option value="">Select language...</option>
          <option value="English">English</option>
          <option value="German">German</option>
          <option value="French">French</option>
          <option value="Italian">Italian</option>
        </select>
        {errors.contract_language && <p className="text-red-500 text-sm mt-1">{errors.contract_language}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Canton for Business Day Definition *</label>
        <input
          type="text"
          value={formData.business_day_canton}
          onChange={(e) => handleChange('business_day_canton', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg ${errors.business_day_canton ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="e.g., Zurich"
        />
        {errors.business_day_canton && <p className="text-red-500 text-sm mt-1">{errors.business_day_canton}</p>}
        <p className="text-sm text-gray-600 mt-1">Determines which public holidays apply</p>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Notice Addresses</h3>
        
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-3">Client Notice Address</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Physical Address *</label>
                <input
                  type="text"
                  value={formData.client_notice_physical}
                  onChange={(e) => handleChange('client_notice_physical', e.target.value)}
                  className={`w-full px-3 py-2 border rounded ${errors.client_notice_physical ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Full address for official notices"
                />
                {errors.client_notice_physical && <p className="text-red-500 text-sm mt-1">{errors.client_notice_physical}</p>}
              </div>
              <div>
                <label className="block text-sm mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.client_notice_email}
                  onChange={(e) => handleChange('client_notice_email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded ${errors.client_notice_email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Official email for notices"
                />
                {errors.client_notice_email && <p className="text-red-500 text-sm mt-1">{errors.client_notice_email}</p>}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium mb-3">Freelancer Notice Address</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Physical Address *</label>
                <input
                  type="text"
                  value={formData.freelancer_notice_physical}
                  onChange={(e) => handleChange('freelancer_notice_physical', e.target.value)}
                  className={`w-full px-3 py-2 border rounded ${errors.freelancer_notice_physical ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Full address for official notices"
                />
                {errors.freelancer_notice_physical && <p className="text-red-500 text-sm mt-1">{errors.freelancer_notice_physical}</p>}
              </div>
              <div>
                <label className="block text-sm mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.freelancer_notice_email}
                  onChange={(e) => handleChange('freelancer_notice_email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded ${errors.freelancer_notice_email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Official email for notices"
                />
                {errors.freelancer_notice_email && <p className="text-red-500 text-sm mt-1">{errors.freelancer_notice_email}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSchedulesSection = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Schedule A:</strong> Detailed project specifications (required)<br/>
          <strong>Schedule B:</strong> Data Processing Agreement (only if processing personal data)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Detailed Services Description *</label>
        <textarea
          value={formData.schedule_a_services}
          onChange={(e) => handleChange('schedule_a_services', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg h-32 ${errors.schedule_a_services ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Comprehensive description of all services, methodologies, approaches..."
        />
        {errors.schedule_a_services && <p className="text-red-500 text-sm mt-1">{errors.schedule_a_services}</p>}
      </div>

      <div className="border-t pt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Specific Deliverables with Acceptance Criteria</h3>
          <button
            onClick={() => addArrayItem('schedule_a_deliverables', { 
              deliverable: '', 
              specification: '', 
              format: '', 
              acceptance: '' 
            })}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            Add Deliverable
          </button>
        </div>
        
        {formData.schedule_a_deliverables.map((item, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg mb-3">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">Deliverable {index + 1}</span>
              <button
                onClick={() => removeArrayItem('schedule_a_deliverables', index)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm mb-1">Deliverable Name</label>
                <input
                  type="text"
                  value={item.deliverable}
                  onChange={(e) => updateArrayItem('schedule_a_deliverables', index, 'deliverable', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Final Website Design"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Specification</label>
                <input
                  type="text"
                  value={item.specification}
                  onChange={(e) => updateArrayItem('schedule_a_deliverables', index, 'specification', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Responsive design, 5 pages, mobile-optimized"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Format</label>
                <input
                  type="text"
                  value={item.format}
                  onChange={(e) => updateArrayItem('schedule_a_deliverables', index, 'format', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Figma files, exported assets"
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Acceptance Criteria</label>
                <input
                  type="text"
                  value={item.acceptance}
                  onChange={(e) => updateArrayItem('schedule_a_deliverables', index, 'acceptance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Matches approved mockups, passes usability testing"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Technical Requirements (optional)</label>
        <textarea
          value={formData.schedule_a_technical}
          onChange={(e) => handleChange('schedule_a_technical', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
          placeholder="Technical specifications, platforms, tools, frameworks, compatibility requirements..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Detailed Project Timeline *</label>
        <textarea
          value={formData.schedule_a_timeline}
          onChange={(e) => handleChange('schedule_a_timeline', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg h-24 ${errors.schedule_a_timeline ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Detailed milestone schedule with specific dates..."
        />
        {errors.schedule_a_timeline && <p className="text-red-500 text-sm mt-1">{errors.schedule_a_timeline}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Dependencies and Assumptions (optional)</label>
        <textarea
          value={formData.schedule_a_dependencies}
          onChange={(e) => handleChange('schedule_a_dependencies', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
          placeholder="Dependencies on client, third parties, or external factors..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Scope Exclusions (optional)</label>
        <textarea
          value={formData.schedule_a_exclusions}
          onChange={(e) => handleChange('schedule_a_exclusions', e.target.value)}
          className="w-full px-3 py-2 border border-gray-// Continuation of the renderSchedulesSection function
300 rounded-lg h-24"
          placeholder="Explicitly state what is NOT included in scope..."
        />
      </div>

      {formData.dpa_required && (
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Schedule B: Data Processing Agreement</h3>
          
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={formData.schedule_b_included}
                onChange={(e) => handleChange('schedule_b_included', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium">Include Data Processing Agreement</span>
            </label>
          </div>

          {formData.schedule_b_included && (
            <div>
              <label className="block text-sm font-medium mb-2">DPA Content</label>
              <textarea
                value={formData.schedule_b_content}
                onChange={(e) => handleChange('schedule_b_content', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32"
                placeholder="Enter Data Processing Agreement details or reference standard DPA template..."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderSignaturesSection = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-sm text-green-800">
          <strong>Final Step:</strong> Complete signatory information. After generating the contract, both parties should review and sign the document.
        </p>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Client Signatory</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Signatory Name *</label>
            <input
              type="text"
              value={formData.client_signatory_name}
              onChange={(e) => handleChange('client_signatory_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.client_signatory_name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Full name of person signing"
            />
            {errors.client_signatory_name && <p className="text-red-500 text-sm mt-1">{errors.client_signatory_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              value={formData.client_signatory_title}
              onChange={(e) => handleChange('client_signatory_title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.client_signatory_title ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="e.g., CEO, Director"
            />
            {errors.client_signatory_title && <p className="text-red-500 text-sm mt-1">{errors.client_signatory_title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Signature Date *</label>
            <input
              type="date"
              value={formData.client_signature_date}
              onChange={(e) => handleChange('client_signature_date', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.client_signature_date ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.client_signature_date && <p className="text-red-500 text-sm mt-1">{errors.client_signature_date}</p>}
          </div>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold mb-4">Freelancer Signatory</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Signatory Name *</label>
            <input
              type="text"
              value={formData.freelancer_signatory_name}
              onChange={(e) => handleChange('freelancer_signatory_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_signatory_name ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Full name of freelancer"
            />
            {errors.freelancer_signatory_name && <p className="text-red-500 text-sm mt-1">{errors.freelancer_signatory_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Signature Date *</label>
            <input
              type="date"
              value={formData.freelancer_signature_date}
              onChange={(e) => handleChange('freelancer_signature_date', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.freelancer_signature_date ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.freelancer_signature_date && <p className="text-red-500 text-sm mt-1">{errors.freelancer_signature_date}</p>}
          </div>
        </div>
      </div>
    </div>
  );
const renderBrandingSection = () => (
     <div className="space-y-6">
       <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
         <p className="text-sm text-blue-800">
           <strong>Final Step:</strong> Customize the appearance of your contract document.
         </p>
       </div>

       <div>
         <label className="block text-sm font-medium mb-2">Contract Title</label>
         <input
           type="text"
           value={formData.contract_title}
           onChange={(e) => handleChange('contract_title', e.target.value)}
           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
           placeholder="Freelance Services Agreement"
         />
       </div>

       <div>
         <label className="block text-sm font-medium mb-2">Logo URL (optional)</label>
         <input
           type="text"
           value={formData.logo_url}
           onChange={(e) => handleChange('logo_url', e.target.value)}
           className="w-full px-3 py-2 border border-gray-300 rounded-lg"
           placeholder="https://supabase.horoboro.com/storage/v1/object/public/horoboro-website/website-images/homepage/animated-logo/Animaz30060.webp"
         />
         <p className="text-sm text-gray-600 mt-1">
           Enter a URL to your logo image. It will appear on the cover page and in headers.
         </p>
         {formData.logo_url && (
           <div className="mt-3">
             <p className="text-sm font-medium mb-2">Preview:</p>
             <img 
               src={formData.logo_url} 
               alt="Logo preview" 
               className="max-w-xs max-h-24 object-contain border border-gray-200 rounded p-2"
               onError={(e) => {
                 e.target.style.display = 'none';
               }}
             />
           </div>
         )}
       </div>
     </div>
   );

const renderCurrentSection = () => {
    switch (sections[currentSection].id) {
      case 'parties': return renderPartiesSection();
      case 'scope': return renderScopeSection();
      case 'term': return renderTermSection();
      case 'compensation': return renderCompensationSection();
      case 'ip': return renderIPSection();
      case 'contractor': return renderContractorSection();
      case 'non_compete': return renderNonCompeteSection();
      case 'data': return renderDataSection();
      case 'dispute': return renderDisputeSection();
      case 'general': return renderGeneralSection();
      case 'schedules': return renderSchedulesSection();
      case 'signatures': return renderSignaturesSection();
      case 'branding': return renderBrandingSection();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <FileText size={32} className="text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Swiss Freelance Contract Generator</h1>
              <p className="text-sm text-gray-600">Complete all required fields to generate your contract</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Contract Sections</h2>
              <nav className="space-y-1">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => setCurrentSection(index)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      currentSection === index
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-lg">{section.icon}</span>
                    <span className="text-sm">{section.title}</span>
                    {currentSection > index && (
                      <CheckCircle size={16} className="ml-auto text-green-600" />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm">
              {/* Section Header */}
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{sections[currentSection].icon}</span>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {sections[currentSection].title}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Section {currentSection + 1} of {sections.length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Section Content */}
              <div className="px-6 py-6">
                {renderCurrentSection()}
              </div>

              {/* Navigation Buttons */}
              <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
                <button
                  onClick={prevSection}
                  disabled={currentSection === 0}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <div className="flex gap-3">
                  {currentSection === sections.length - 1 ? (
                    <button
                      onClick={() => setShowPreview(true)}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      <Eye size={18} />
                      Preview & Generate Contract
                    </button>
                  ) : (
                    <button
                      onClick={nextSection}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle size={20} className="text-red-600 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-red-800">Please fix the following errors:</h3>
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                      {Object.keys(errors).map(key => (
                        <li key={key}>{errors[key]}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    {showPreview && (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full h-full max-w-full max-h-full flex flex-col">
        <ContractPDFPreview 
            formData={formData} 
            onClose={() => setShowPreview(false)}
            onGeneratePDF={generatePDFDirect}  // Add this line
        />
        </div>
    </div>
    )}
    </div>
  );
};

export default ContractFormApp;
